import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Gift, Link2, Package, Percent, ShoppingCart } from 'lucide-react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import Content from '../components/layout/Content';
import BookingFunnelCMS from './cms/BookingFunnelCMS';
import BundlesCMS from './cms/BundlesCMS';
import DeeplinksCMS from './cms/DeeplinksCMS';
import DiscountCodesCMS from './cms/DiscountCodesCMS';
import ServicesCMS from './cms/ServicesCMS';

type CMSModuleCardProps = {
    title: string;
    description: string;
    icon: React.ElementType;
    onClick: () => void;
};

const CMSModuleCard = ({ title, description, icon: Icon, onClick }: CMSModuleCardProps) => {
    return (
        <Card className='cursor-pointer hover:shadow-lg transition-shadow' onClick={onClick}>
            <CardHeader className='flex flex-row items-center gap-4'>
                <div className='p-2 bg-primary/10 rounded-lg'>
                    <Icon className='h-6 w-6 text-primary' />
                </div>
                <div>
                    <CardTitle className='text-lg'>{title}</CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                <CardDescription className='mb-4'>{description}</CardDescription>
                <div className='flex justify-end'>
                    <Button variant='ghost' size='sm'>
                        <ArrowRight className='h-4 w-4' />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const CMSHome = () => {
    const navigate = useNavigate();

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-3xl font-bold mb-2'>CMS</h1>
                <p className='text-muted-foreground mb-8'>
                    Verwalten Sie Inhalte für verschiedene Bereiche der Anwendung.
                </p>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                    <CMSModuleCard
                        title='Buchbare Leistungen'
                        description='Verwalten Sie buchbare Leistungen, die in verschiedenen Bereichen der Anwendung verwendet werden.'
                        icon={Package}
                        onClick={() => navigate('/cms/services')}
                    />
                    <CMSModuleCard
                        title='Bundles'
                        description='Verwalten Sie Bundles für Behandlungsvarianten. Kunden können mehrere Sitzungen zu einem reduzierten Preis kaufen.'
                        icon={Gift}
                        onClick={() => navigate('/cms/bundles')}
                    />
                    <CMSModuleCard
                        title='Booking Funnel'
                        description='Verwalten Sie Texte, Bilder und Einstellungen für den Buchungs-Funnel.'
                        icon={ShoppingCart}
                        onClick={() => navigate('/cms/booking-funnel')}
                    />
                    <CMSModuleCard
                        title='Rabattcodes'
                        description='Verwalten Sie Rabattcodes mit Gültigkeitszeiträumen und zugewiesenen Behandlungsvarianten.'
                        icon={Percent}
                        onClick={() => navigate('/cms/discount-codes')}
                    />
                    <CMSModuleCard
                        title='Deeplinks'
                        description='Verwalten Sie Deeplinks mit eindeutigem Slug und zugeordneter buchbarer Leistung.'
                        icon={Link2}
                        onClick={() => navigate('/cms/deeplinks')}
                    />
                </div>
            </div>
        </Content>
    );
};

const CMS = () => {
    return (
        <Routes>
            <Route path='/' element={<CMSHome />} />
            <Route path='/services/*' element={<ServicesCMS />} />
            <Route path='/bundles/*' element={<BundlesCMS />} />
            <Route path='/booking-funnel/*' element={<BookingFunnelCMS />} />
            <Route path='/discount-codes/*' element={<DiscountCodesCMS />} />
            <Route path='/deeplinks/*' element={<DeeplinksCMS />} />
        </Routes>
    );
};

export default CMS;
